import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IIdentityAuthViewModel } from 'viewModels';
import { NavHeader, Button } from '../../components';
import LogoVigna from '../../assets/images/logoHUB.png';
import LogoAdv from '../../assets/images/logoHUB-adv.png';
import dottedImage from '../../assets/images/path-to-dotted-pattern.png';
import HeroImage from '../../assets/images/CellPhoneHUB.png';
import AboutImage from '../../assets/images/about-image.jpg';
import { ReactComponent as Facebook } from '../../assets/icons/Icon-facebook.svg';
import { ReactComponent as Instagram } from '../../assets/icons/Icon-instagram.svg';
import { useApp, useAuth } from '../../context';
import { decryptFromAPI } from '../../utils/crypto';
import { ReactComponent as Linkedin } from '../../assets/icons/Icon-linkedin.svg';
import {
  Container,
  HeroSection,
  FindLawyerSection,
  FeaturesSection,
  FreeExperienceSection,
  TestimonialsSection,
  AboutSection,
  FAQSection,
  Footer,
  FindCorrespondentContainer,
} from './styles';
import { FindUser } from '../../components/FindUser';
import { FaHandshake, FaBalanceScale, FaRocket } from 'react-icons/fa';
import { useConfig } from '../../context/configCustom';

export function Home() {
  const { pathResourceEmail } = useConfig();
  const navigate = useNavigate();
  const { loginWithIdentity } = useAuth();
  const { pathname, hash, key, search } = useLocation();
  const { setLoading } = useApp();

  useEffect(() => {
    const encoded = new URLSearchParams(search).get('auth');
    if (encoded !== '' && encoded !== null) {
      const decode = decodeURIComponent(`${encoded}`);
      const auth = decryptFromAPI(decode) as IIdentityAuthViewModel;
      setLoading(true);
      loginWithIdentity(auth)
        .then(() => {
          if (!auth.user?.document) {
            navigate('/naturalPersonAccount');
            return;
          }
          navigate('/feed');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [search, loginWithIdentity, setLoading, navigate]);

  useEffect(() => {
    if (hash === '') {
      window.scrollTo(0, 0);
    } else {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView();
        }
      }, 0);
    }
  }, [pathname, hash, key]);

  return (
    <HelmetProvider>
      <Container>
        <Helmet>
          <meta
            name="description"
            content="A 1ª plataforma jurídica completa, que conecta você com os melhores profissionais, conteúdos, clientes, e serviços jurídicos."
          />
          <meta name="keywords" content="legalhub, legalcontrol, advogados, serviços jurídicos" />
          <title>Legal Hub - Home</title>
        </Helmet>

        <NavHeader typeHeader="loggedOut" />

        <HeroSection>
          <div className="content">
            <h1>
              A 1ª plataforma jurídica completa, que conecta você com os melhores profissionais,
              conteúdos, clientes, e serviços jurídicos.
            </h1>
            <Button
              text="INSCREVA-SE E USE GRATUITAMENTE"
              font={{ weight: 700 }}
              background="tertiary_500"
              color="background_white"
              border={{ color: 'tertiary_500', width: 1 }}
              textHoverColor="background_white"
              backgroundHoverColor="primary"
              type="button"
              onClick={() => navigate('/register')}
            />
          </div>
          <img src={HeroImage} alt="Mão segurando celular com advogada" className="hero-image" />
        </HeroSection>

        <FindLawyerSection>
          <h2>Você sabia que pode encontrar o advogado ideal para o seu caso em minutos?</h2>
          <FindCorrespondentContainer className="find-correspondent">
            <FindUser redirectOnDetails />
          </FindCorrespondentContainer>
          <div className="search-form" />
        </FindLawyerSection>

        <FeaturesSection>
          <h2>Principais recursos</h2>
          <div className="features">
            <div className="feature-card">
              <h3>Conexão Direta com Advogados</h3>
              <div className="icon">
                <FaHandshake />
              </div>
              <p>
                Encontre o especialista certo para seu caso e negocie honorários diretamente, sem
                intermediários.
              </p>
            </div>
            <div className="feature-card">
              <h3>Gestão Inteligente de Processos</h3>
              <div className="icon">
                <FaBalanceScale />
              </div>
              <p>Acompanhe seus processos em tempo real com transparência e controle total.</p>
            </div>
            <div className="feature-card">
              <h3>Soluções Jurídicas Inovadoras</h3>
              <div className="icon">
                <FaRocket />
              </div>
              <p>
                Ferramentas que otimizam o tempo e reduzem custos, garantindo eficiência na sua
                demanda jurídica.
              </p>
            </div>
          </div>
        </FeaturesSection>

        <FreeExperienceSection>
          <div className="background-overlay" />
          <h2>Acima de tudo, oferecemos essa experiência de forma totalmente gratuita</h2>
          <ul>
            <li>Sem mensalidades</li>
            <li>Sem complicação</li>
            <li>Sem intermediários</li>
            <li>Sem taxas ocultas</li>
          </ul>
          <Button
            text="INSCREVA-SE AGORA"
            dimensions={{ width: '20rem', height: '3rem' }}
            font={{ weight: 600 }}
            background="tertiary_500"
            color="background_white"
            border={{ color: 'tertiary_500', width: 1 }}
            textHoverColor="background_white"
            backgroundHoverColor="primary"
            onClick={() => navigate('/register')}
          />
          <img src={dottedImage} alt="Pontilhado" className="path-dotted" />
        </FreeExperienceSection>

        <TestimonialsSection>
          <h2>Depoimentos</h2>
          <div className="testimonials">
            <div className="testimonial-card">
              <p>
                A plataforma me conectou a clientes de forma direta, sem intermediários. Agora,
                gerencio meus casos com mais praticidade e foco no que realmente importa!
              </p>
              <p className="author">Dr. Lucas S., Advogado Empresarial</p>
            </div>
            <div className="testimonial-card">
              <p>
                Nunca foi tão fácil acompanhar os processos da empresa. O LegalHUB trouxe mais
                controle e segurança para nossa equipe jurídica!
              </p>
              <p className="author">Carlos F., Gestor de Compliance</p>
            </div>
            <div className="testimonial-card">
              <p>
                O LegalHub revolucionou a forma como lidamos com o jurídico. Mais eficiência, menos
                burocracia.
              </p>
              <p className="author">Pedro T., CEO</p>
            </div>
          </div>
        </TestimonialsSection>

        <AboutSection>
          <img src={AboutImage} alt="Duas pessoas em um escritório" className="about-image" />
          <div className="content">
            <h2>Sobre Nós</h2>
            <p>
              O LEGAL HUB.ADV.BR conecta clientes e empresas a advogados especializados, permitindo
              a negociação direta de honorários e a gestão inteligente de processos.
            </p>
            <p>
              Com tecnologia e inovação, eliminamos burocracias para tornar o acesso à advocacia
              mais rápido, transparente e eficiente.
            </p>
          </div>
        </AboutSection>

        <FAQSection>
          <h2>FAQ - Perguntas Frequentes</h2>
          <div className="faq-item">
            <h3>1. O que é o LegalHUB?</h3>
            <p>
              O LegalHUB é uma plataforma que conecta empresas e clientes a advogados
              especializados, permitindo a negociação direta de honorários e a gestão eficiente de
              processos jurídicos.
            </p>
          </div>
          <div className="faq-item">
            <h3>2. O LegalHUB é gratuito?</h3>
            <p>
              Sim! O LegalHUB é 100% gratuito para clientes e empresas que buscam advogados. Sem
              mensalidades, sem taxas ocultas e sem burocracia.
            </p>
          </div>
          <div className="faq-item">
            <h3>3. Como encontro um advogado na plataforma?</h3>
            <p>
              Basta se cadastrar, informar sua necessidade jurídica e a plataforma conectará você a
              advogados especializados na área desejada.
            </p>
          </div>
          <div className="faq-item">
            <h3>4. Posso negociar os honorários diretamente com o advogado?</h3>
            <p>
              Sim! No LegalHUB, a negociação dos honorários acontece diretamente entre o cliente e o
              advogado, sem intermediários.
            </p>
          </div>
          <div className="faq-item">
            <h3>5. O LegalHUB é seguro?</h3>
            <p>
              Sim! Nossa plataforma prioriza a segurança dos dados e a privacidade de todos os
              usuários, garantindo um ambiente confiável para clientes e advogados.
            </p>
          </div>
          <div className="faq-item">
            <h3>6. Como os advogados são selecionados na plataforma?</h3>
            <p>
              Os advogados cadastrados no LegalHUB passam por um processo de verificação para
              garantir que sejam devidamente registrados na OAB e possuam experiência na área de
              atuação.
            </p>
          </div>
          <div className="faq-item">
            <h3>7. O LegalHUB atende quais áreas do Direito?</h3>
            <p>
              Nossa plataforma conecta clientes a advogados em todas as áreas, incluindo Direito
              Empresarial, Trabalhista, Civil, Tributário, Penal, Família e muito mais.
            </p>
          </div>
        </FAQSection>

        <Footer>
          <div className="contact">
            <h3>Fale conosco</h3>
            <p>
              <strong>E-MAIL</strong> contato@legalhub.adv.br
            </p>
            <p>
              <strong>SOCIAL</strong>
            </p>
            <div className="social-icons">
              <a
                href="https://www.facebook.com/profile.php?id=61569360665904"
                target="_blank"
                rel="noreferrer">
                <Facebook />
              </a>
              <a href="https://www.instagram.com/legalhub.adv.br/" target="_blank" rel="noreferrer">
                <Instagram />
              </a>
              <a
                href="https://www.linkedin.com/company/legal-hub-adv/?viewAsMember=true"
                target="_blank"
                rel="noreferrer">
                <Linkedin />
              </a>
            </div>
          </div>
          <div className="logo">
            <img
              src={pathResourceEmail === 'TemplatesVigna' ? LogoVigna : LogoAdv}
              alt="LegalHUB by LegalControl"
            />
          </div>
        </Footer>
      </Container>
    </HelmetProvider>
  );
}
